import { z } from 'zod';
import { JOB_NEGOTIATION_TERM_KEYS } from '~/features/market/negotiations/utils';
import {
  ServiceCounterInputSchema,
  type ServiceCounterAvailability,
  type ServiceCounterInput,
  type ServiceOpportunity,
} from './schemas';

export type ServiceCounterDraft = {
  expectedNegotiationId: number;
  version: number;
  terms: ServiceCounterInput['negotiationTerms'];
  messageBody: string;
  window: { start: string; end: string };
  entries: ServiceCounterAvailability['entries'];
  selectedIds: number[];
  selectionLoaded: boolean;
  selectionInitialized: boolean;
  reviewed: ServiceCounterInput | null;
  focusOnOpen: boolean;
};

export const counterWindowSchema = z
  .object({ start: z.iso.date(), end: z.iso.date() })
  .refine(
    ({ start, end }) => start <= end && Date.parse(end) - Date.parse(start) <= 365 * 86_400_000
  );

type Term = (typeof JOB_NEGOTIATION_TERM_KEYS)[number];
export const isServiceCounterIntegerTerm = (key: Term) =>
  key.startsWith('num') ||
  [
    'minRvusPerShift',
    'shiftPeriod',
    'shiftsPerPeriod',
    'callShiftsPerPeriod',
    'rvusPerPeriod',
    'rvuBonusMinimum',
  ].includes(key);

const present = (value: unknown) => value !== null && value !== undefined && value !== '';
const positive = (value: unknown) =>
  present(value) && Number.isFinite(Number(value)) && Number(value) > 0;
const validInteger = (value: unknown) =>
  Number.isSafeInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 2_147_483_647;
const positiveInt = (value: unknown) => positive(value) && validInteger(value);
const MONEY_DECIMAL = /^\d{1,6}(?:\.\d{1,2})?$/;
const HOURS_DECIMAL = /^\d{1,2}(?:\.\d)?$/;
const fitsDecimal = (value: unknown, pattern: RegExp) =>
  (typeof value === 'string' || typeof value === 'number') && pattern.test(String(value));

const moneyFields = JOB_NEGOTIATION_TERM_KEYS.filter(
  (key) => key.startsWith('pricePer') || key.endsWith('RateDiff') || key === 'rvuBonusAmt'
);

/** Fields the counter API requires before it can calculate a new offer. */
export const counterFieldErrors = (values: Record<string, unknown>): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (typeof values.messageBody !== 'string' || !values.messageBody.trim())
    errors.messageBody = 'Enter a counteroffer message.';
  if (!values.startDate) errors.startDate = 'Choose a start date.';
  if (!positive(values.hoursPerShift))
    errors.hoursPerShift = 'Enter hours per shift greater than zero.';
  if (values.laborReqType === 'FTE' && !positiveInt(values.numOfShifts))
    errors.numOfShifts = 'Enter a whole number of shifts greater than zero.';
  if (values.laborReqType === 'RVU') {
    if (!positiveInt(values.numRvusReq))
      errors.numRvusReq = 'Enter required RVUs greater than zero.';
    if (!positiveInt(values.minRvusPerShift))
      errors.minRvusPerShift = 'Enter minimum RVUs per shift greater than zero.';
  }
  if (!values.laborReqType) errors.laborReqType = 'Choose a labor requirement type.';
  if (!values.compType) errors.compType = 'Choose a compensation type.';
  if (values.compType === 'HOUR' && !positive(values.pricePerHour))
    errors.pricePerHour = 'Enter an hourly rate greater than zero.';
  if (values.compType === 'SHIFT' && !positive(values.pricePerShift))
    errors.pricePerShift = 'Enter a shift rate greater than zero.';
  if (values.compType === 'RVU') {
    if (!positive(values.pricePerRvu))
      errors.pricePerRvu = 'Enter a rate per RVU greater than zero.';
    if (!positiveInt(values.rvusPerPeriod))
      errors.rvusPerPeriod = 'Enter RVUs per period greater than zero.';
  }
  if (present(values.hoursPerShift) && !fitsDecimal(values.hoursPerShift, HOURS_DECIMAL))
    errors.hoursPerShift = 'Use at most 99.9 hours with one decimal place.';
  for (const key of moneyFields) {
    const value = values[key];
    if (present(value) && !fitsDecimal(value, MONEY_DECIMAL))
      errors[key] = 'Use at most 999999.99 with two decimal places.';
  }
  for (const key of JOB_NEGOTIATION_TERM_KEYS) {
    if (
      isServiceCounterIntegerTerm(key) &&
      present(values[key]) &&
      !validInteger(values[key]) &&
      !errors[key]
    )
      errors[key] = 'Enter a whole number from 0 to 2147483647.';
  }
  if (
    typeof values.endDate === 'string' &&
    typeof values.startDate === 'string' &&
    values.endDate &&
    values.startDate &&
    values.endDate < values.startDate
  )
    errors.endDate = 'End date must follow the start date.';
  return errors;
};

/** The editor and payload gate use the same field rules. */
export const serviceCounterFormSchema = z
  .record(z.string(), z.string())
  .superRefine((values, ctx) => {
    for (const [key, message] of Object.entries(counterFieldErrors(values)))
      ctx.addIssue({ code: 'custom', path: [key], message });
  });

export const createServiceCounterDraft = (
  opportunity: ServiceOpportunity
): ServiceCounterDraft | null => {
  const offer = opportunity.latestNegotiation;
  if (!offer) return null;
  const terms = Object.fromEntries(JOB_NEGOTIATION_TERM_KEYS.map((key) => [key, offer.terms[key]]));
  const parsed = ServiceCounterInputSchema.shape.negotiationTerms.safeParse(terms);
  if (!parsed.success) return null;
  const dates = offer.availabilitySelection.map((entry) => entry.date).sort();
  const start = dates[0] ?? offer.startDate;
  const end = dates[dates.length - 1] ?? offer.endDate ?? start;
  const window = counterWindowSchema.safeParse({ start, end });
  if (!window.success) return null;
  return {
    expectedNegotiationId: offer.id,
    version: offer.version,
    terms: parsed.data,
    messageBody: '',
    window: window.data,
    entries: [],
    selectedIds: [],
    selectionLoaded: false,
    selectionInitialized: false,
    reviewed: null,
    focusOnOpen: true,
  };
};

export const serviceCounterPayload = (
  draft: ServiceCounterDraft | null
): ServiceCounterInput | null => {
  if (
    !draft?.selectionLoaded ||
    draft.selectedIds.some((id) => !draft.entries.some((entry) => entry.availabilityEntryId === id))
  )
    return null;
  if (Object.keys(counterFieldErrors({ ...draft.terms, messageBody: draft.messageBody })).length)
    return null;
  const parsed = ServiceCounterInputSchema.safeParse({
    expectedNegotiationId: draft.expectedNegotiationId,
    negotiationTerms: draft.terms,
    availabilityEntryIds: draft.selectedIds,
    messageBody: draft.messageBody,
  });
  return parsed.success ? parsed.data : null;
};

export const invalidateCounterSelection = (
  draft: ServiceCounterDraft | null
): ServiceCounterDraft | null =>
  draft ? { ...draft, entries: [], selectionLoaded: false, reviewed: null } : null;
